import React, { useEffect, useState } from 'react';
import { ICON } from '../../icons/iconIds';
import IconButton from '../icons/IconButton';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { documentDesignService } from '../../services/documentDesignService';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const MediaDeleteModal = ({ item, onClose, onDeleted }) => {
  const [usage, setUsage] = useState(null);
  const [usageError, setUsageError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    documentDesignService
      .getMediaUsage(item.id)
      .then((data) => !cancelled && setUsage(data))
      .catch((err) => !cancelled && setUsageError(err.message));
    return () => {
      cancelled = true;
    };
  }, [item.id]);

  const inUse = usage && (usage.compositions > 0 || usage.documents > 0);

  const remove = async () => {
    setDeleting(true);
    setError(null);
    try {
      const result = await documentDesignService.deleteMedia(item.id);
      onDeleted?.(item.id, result);
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="dd-modal-overlay" onClick={deleting ? undefined : onClose} role="presentation">
      <div onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true" aria-label="Delete from Brand Library">
        <Card className="dd-modal dd-delete-modal">
          <div className="dd-detail-header">
            <h3>Delete from Brand Library?</h3>
            <IconButton icon={ICON.ddClose} onClick={onClose} ariaLabel="Close" disabled={deleting} />
          </div>

          <p className="dd-delete-target">
            <strong title={item.file_name}>{item.file_name}</strong>
            <span className="dd-muted">{item.folder_path || '(root)'}</span>
          </p>

          <div className="dd-danger-box">
            <strong>Caution: this deletes the file from SharePoint.</strong>
            <ul>
              <li>The file is moved to the SharePoint site recycle bin, where it can be restored for a limited time.</li>
              <li>It is removed from the Brand Library for everyone in the group, along with its AI tags and curator tags.</li>
              <li>Stored previews and the full-resolution copy are deleted.</li>
            </ul>
          </div>

          {usageError && <p className="dd-muted">Could not check where this file is used: {usageError}</p>}
          {!usage && !usageError && <p className="dd-muted">Checking where this file is used…</p>}
          {usage && !inUse && <p className="dd-muted">Not used in any Media Creator design or document layout.</p>}
          {inUse && (
            <div className="dd-warning">
              In use by {[
                usage.compositions > 0 && plural(usage.compositions, 'Media Creator design'),
                usage.documents > 0 && plural(usage.documents, 'document layout'),
              ].filter(Boolean).join(' and ')}. Those slots will show empty until another image is chosen
              (document photo slots are re-filled automatically).
            </div>
          )}

          <label className="dd-inline-check dd-delete-confirm">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              disabled={deleting}
            />
            I understand this deletes the file from SharePoint
          </label>

          {error && <div className="dd-error-inline">{error}</div>}

          <div className="dd-detail-actions">
            <Button variant="danger" onClick={remove} loading={deleting} disabled={!confirmed}>
              Delete file
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={deleting}>
              Cancel
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MediaDeleteModal;
